
import React, { useState, useMemo, useCallback, type ReactNode } from 'react';
import {
  INVALID_ERROR,
  BLOCKED_ERROR,
  FORBIDDEN_ERROR,
  SESSION_EXPIRED_ERROR,
} from '@/assets/AppConstants';

import { wasSentWithPreviousWorkspaceScope } from '@/interceptors/workspaceScopeHeaders';

import BlockScreen from '@/views/common/BlockScreen';
import AlertMessage from '@/views/common/AlertMessage';

import AppContext from './context';

const initialAlertProps = { autoHideDuration: 3000 };

const DEFAULT_APP_LOADING = {
  page: false,
  table: false,
  modal: false,
  blockScreen: false,
  invisibleBlockScreen: false,
};
interface AppContextProviderProps {
  children: ReactNode;
}

const AppContextProvider = ({ children }: AppContextProviderProps) => {
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState<string | React.ReactElement | Record<string, any>>('');
  const [alertSeverity, setAlertSeverity] = useState('');

  const [requestStatus, setRequestStatus] = useState(0);
  const [alertProps, setAlertProps] = useState(initialAlertProps);

  const [blockTable, setBlockTable] = useState(false);
  const [loadingBlockScreen, setLoadingBlockScreen] = useState(false);

  const handleRequestStatus = useCallback((status) => setRequestStatus(status), [setRequestStatus]);
  const handleResetStatus = useCallback(() => setRequestStatus(0), [setRequestStatus]);
  const hasRequestStatus = useCallback(() => requestStatus !== 0, [requestStatus]);

  const [loading, setLoading] = useState(DEFAULT_APP_LOADING);

  const setAppLoading = useCallback((value: Record<string, any> = { }) => {
    if (typeof value === 'boolean') {
      setLoading(() => ({
        page: value,
        table: value,
        modal: value,
        blockScreen: value,
        invisibleBlockScreen: value,
      }));

      return;
    }

    setLoading((prev) => ({ ...prev, ...value }));
  }, [setLoading]);

  const closeAlert = () => {
    setAlertMessage('');
    setAlertTitle('');
    setAlertSeverity('');
    setAlertProps(initialAlertProps);
  };

  const openAlert = useCallback(async (message, severity, title = '', openAlertProps) => {
    const normalizedSeverity = typeof severity === 'string' ? severity : 'error';

    if (React.isValidElement(message)) {
      setAlertMessage(message);
    } else if (typeof (message) === 'string') {
      setAlertMessage(message);
    } else {
      const status = message?.response?.status;
      // Still in flight across a workspace or client switch: its 403 is about the old scope, not this screen.
      if (status === FORBIDDEN_ERROR && wasSentWithPreviousWorkspaceScope(message)) return;

      let data: Record<string, any> = {};

      if (message?.response) {
        if (message.response?.data instanceof Blob) {
          data = JSON.parse(await message.response.data.text());
        } else {
          data = message.response.data;
        }

        if ([FORBIDDEN_ERROR, SESSION_EXPIRED_ERROR, BLOCKED_ERROR, INVALID_ERROR].includes(status)) {
          handleRequestStatus(status);
        } else {
          setAlertMessage(data);
        }
      } else {
        setAlertMessage(message?.message || String(message));
      }
    }
    setAlertSeverity(normalizedSeverity);
    setAlertTitle(title);

    if (openAlertProps) setAlertProps(openAlertProps);
  }, [handleRequestStatus, setAlertMessage, setAlertSeverity, setAlertTitle, setAlertProps]);

  const handleBlockScreen = (blocked, screen = false) => {
    setLoadingBlockScreen(() => blocked);
    setTimeout(() => {
      setBlockTable(() => screen);
    }, !screen ? 1000 : 0);
  };

  const appValue = useMemo(() => ({
    hasBlockScreen: loadingBlockScreen,
    hasBlockTable: blockTable,
    blockScreen: handleBlockScreen,
    handleAlert: openAlert,

    isLoading: loading,
    setAppLoading,

    requestStatus,
    hasRequestStatus,
    handleResetStatus,
    handleRequestStatus,
  }), [
    loadingBlockScreen,
    blockTable,
    requestStatus,
    loading,
    setAppLoading,
    hasRequestStatus,
    handleResetStatus,
    handleRequestStatus,
    openAlert,
  ]);

  const openBlockScreen = loading.blockScreen || loadingBlockScreen;
  const openInvisibleBlockScreen = loading.page || loading.invisibleBlockScreen;

  return (
    <AppContext.Provider value={appValue}>
      {children}
      {
        openBlockScreen && (
          <BlockScreen openBlockScreen={openBlockScreen} />
        )
      }
      {
        openInvisibleBlockScreen && (
          <BlockScreen openBlockScreen={openInvisibleBlockScreen} invisible />
        )
      }
      {
        (alertMessage !== '' && alertSeverity !== '') && (
          <AlertMessage
            alertTitle={alertTitle}
            alertMessage={alertMessage}
            alertSeverity={alertSeverity}
            openAlert={(alertMessage !== '')}
            closeAlert={closeAlert}
            snackbarProps={alertProps}
          />
        )
      }
    </AppContext.Provider>
  );
};
export default AppContextProvider;
